import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowLeft, Send, BookOpen, User, GraduationCap, MapPin, Briefcase, Building2, HelpCircle, UserCheck, ChevronDown, Calendar } from "lucide-react";
import { CORSI } from "@/data/corsiData";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MESI_IT = {
  gennaio: 0, febbraio: 1, marzo: 2, aprile: 3, maggio: 4, giugno: 5,
  luglio: 6, agosto: 7, settembre: 8, ottobre: 9, novembre: 10, dicembre: 11,
};

// Converte una data in formato "13 Aprile 2026" in timestamp, per poterle ordinare cronologicamente
function parseDataItaliana(str) {
  if (!str) return null;
  const m = str.trim().toLowerCase().match(/(\d{1,2})\s+([a-zàéìòù]+)\s+(\d{4})/i);
  if (!m) return null;
  const meseIdx = MESI_IT[m[2]];
  if (meseIdx === undefined) return null;
  return new Date(Number(m[3]), meseIdx, Number(m[1])).getTime();
}

const emptyForm = {
  corso_slug: "",
  corso_titolo: "",
  data_inizio_corso: "",
  tipo_utente: "",
  nome: "",
  cognome: "",
  luogo_nascita: "",
  data_nascita: "",
  codice_fiscale: "",
  indirizzo_residenza: "",
  cap_residenza: "",
  telefono: "",
  email: "",
  occupazione: "",
  ragione_sociale: "",
  sede_legale: "",
  telefono_azienda: "",
  pec: "",
  codice_sdi: "",
  piva_cf: "",
  referente: "",
  telefono_referente: "",
  email_referente: "",
  come_ci_hai_conosciuto: "",
};

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section data-testid={testId} className="rounded-2xl bg-white border border-[var(--confcom-border)] p-6 md:p-8 mb-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-[#EBF5FF] flex items-center justify-center">
          <Icon size={18} className="text-[var(--confcom-blue)]" aria-hidden="true" />
        </div>
        <h2 className="text-lg md:text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, required = false, placeholder = "", colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "md:col-span-2" : ""}>
      <label htmlFor={name} className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={`iscrizione-field-${name}`}
        className="w-full rounded-lg border border-[var(--confcom-border)] bg-white px-3.5 py-2.5 text-sm text-[var(--confcom-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)]/30 focus:border-[var(--confcom-blue)] transition-colors"
      />
    </div>
  );
}

export default function IscrizioneCorsoPage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const slugParam = params.get("corso") || "";
  const dataParam = params.get("data") || "";

  const corsoDaSlug = useMemo(() => CORSI.find((c) => c.slug === slugParam), [slugParam]);

  const [data, setData] = useState(() => ({
    ...emptyForm,
    corso_slug: corsoDaSlug?.slug || "",
    corso_titolo: corsoDaSlug?.titolo || "",
  }));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fatturazioneOpen, setFatturazioneOpen] = useState(false);

  // Sessioni con date alternative di inizio corso (come nella pagina di dettaglio corso)
  const sessioni = corsoDaSlug?.sessioni && corsoDaSlug.sessioni.length > 1 ? corsoDaSlug.sessioni : null;

  // Se l'utente ha già scelto una data nella pagina di dettaglio corso (passata via URL), la usiamo come default;
  // altrimenti default sulla data che parte prima
  const defaultSessioneIdx = useMemo(() => {
    if (!sessioni) return 0;
    if (dataParam) {
      const idxDaParam = sessioni.findIndex((s) => s.dataInizio === dataParam);
      if (idxDaParam !== -1) return idxDaParam;
    }
    let bestIdx = 0;
    let bestTime = parseDataItaliana(sessioni[0].dataInizio) ?? Infinity;
    sessioni.forEach((s, i) => {
      const t = parseDataItaliana(s.dataInizio);
      if (t !== null && t < bestTime) { bestTime = t; bestIdx = i; }
    });
    return bestIdx;
  }, [sessioni, dataParam]);

  const [sessioneIdx, setSessioneIdx] = useState(defaultSessioneIdx);

  useEffect(() => {
    setSessioneIdx(defaultSessioneIdx);
  }, [corsoDaSlug?.slug, defaultSessioneIdx]);

  // Mantiene sincronizzata la data di inizio selezionata con i dati del form
  useEffect(() => {
    if (!corsoDaSlug) return;
    const dataInizioSelezionata = sessioni ? sessioni[sessioneIdx]?.dataInizio : corsoDaSlug.dataInizio;
    setData((prev) => ({ ...prev, data_inizio_corso: dataInizioSelezionata || "" }));
  }, [corsoDaSlug, sessioneIdx, sessioni]);

  useEffect(() => {
    document.title = corsoDaSlug
      ? `Iscrizione al ${corsoDaSlug.titolo} | Confcommercio Roma`
      : "Iscrizione al corso | Confcommercio Roma";
    window.scrollTo(0, 0);
  }, [corsoDaSlug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCorsoSelect = (e) => {
    const slug = e.target.value;
    const c = CORSI.find((x) => x.slug === slug);
    setData((prev) => ({ ...prev, corso_slug: slug, corso_titolo: c?.titolo || "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!data.corso_slug || !data.corso_titolo) {
      setErrorMsg("Seleziona un corso prima di procedere.");
      return;
    }
    if (!data.tipo_utente) {
      setErrorMsg("Indica se sei socio o non socio di Confcommercio Roma prima di procedere.");
      const el = document.querySelector('[data-testid="section-profilazione"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/formazione/iscrizione`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await res.json();
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrorMsg("Si è verificato un errore nell'invio. Riprova più tardi o contatta la segreteria.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="iscrizione-success" className="bg-[var(--confcom-bg-subtle,#F5F7FA)] min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-6">
            <CheckCircle2 size={32} className="text-green-600" aria-hidden="true" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-4">
            Iscrizione inviata
          </h1>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mt-4 max-w-xl mx-auto">
            Grazie! Abbiamo ricevuto la tua richiesta di iscrizione a <strong>{data.corso_titolo}</strong>.
            La nostra segreteria didattica ti contatterà a breve per confermare la partecipazione e fornirti i dettagli.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/formazione/corsi-in-partenza"
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--confcom-blue)] text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity"
              data-testid="iscrizione-back-corsi"
            >
              <ArrowLeft size={16} /> Torna ai corsi
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" data-testid="iscrizione-corso-page" className="bg-[#F5F7FA]">
      {/* Hero */}
      <section className="bg-[var(--confcom-blue)] text-white" data-testid="iscrizione-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-16 pb-8 md:pb-10">
          <Link to={corsoDaSlug ? `/formazione/corsi-in-partenza/${corsoDaSlug.slug}` : "/formazione/corsi-in-partenza"} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-4">
            <ArrowLeft size={16} /> {corsoDaSlug ? "Torna al corso" : "Torna ai corsi"}
          </Link>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">
              Domanda di iscrizione
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mt-2 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Iscrizione al Corso
          </h1>
        </div>
      </section>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="iscrizione-corso-form">
        {/* Corso */}
        {corsoDaSlug ? (
          <Section icon={GraduationCap} title="Ti stai iscrivendo a" testId="section-corso">
            <p
              className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5"
              data-testid="iscrizione-corso-titolo"
            >
              {corsoDaSlug.titolo}
            </p>

            {/* Data di iscrizione (data del corso selezionata) */}
            <div className="max-w-xs">
              <p style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--confcom-text-muted)", marginBottom: sessioni ? "0.4rem" : "0.15rem" }}>
                {sessioni ? "Sessione di partenza" : "Data di iscrizione"}
              </p>
              {sessioni ? (
                <Select
                  value={String(sessioneIdx)}
                  onValueChange={(v) => setSessioneIdx(Number(v))}
                >
                  <SelectTrigger
                    className="h-9 text-sm font-semibold text-[var(--confcom-text-primary)] border-[var(--confcom-border)] focus:ring-[var(--confcom-blue)] data-[state=open]:border-[var(--confcom-blue)]"
                    data-testid="iscrizione-select-data-inizio"
                    aria-label="Seleziona la data di iscrizione al corso"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-[var(--confcom-blue)] shrink-0" aria-hidden="true" />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {sessioni.map((s, i) => (
                      <SelectItem
                        key={i}
                        value={String(i)}
                        className="focus:bg-[#EBF5FF] focus:text-[var(--confcom-blue)] data-[state=checked]:text-[var(--confcom-blue)] data-[state=checked]:font-semibold [&_svg]:text-[var(--confcom-blue)]"
                        data-testid={`iscrizione-select-data-inizio-opt-${i}`}
                      >
                        {s.dataInizio}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                  <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "var(--confcom-text-primary)" }}>{corsoDaSlug.dataInizio}</span>
                </div>
              )}
            </div>
          </Section>
        ) : (
          <Section icon={BookOpen} title="Corso di interesse" testId="section-corso">
            <div>
              <label htmlFor="corso_slug" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                A quale corso sei interessato?<span className="text-red-500 ml-0.5">*</span>
              </label>
              <select
                id="corso_slug"
                name="corso_slug"
                value={data.corso_slug}
                onChange={handleCorsoSelect}
                required
                data-testid="iscrizione-field-corso_slug"
                className="w-full rounded-lg border border-[var(--confcom-border)] bg-white px-3.5 py-2.5 text-sm text-[var(--confcom-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)]/30 focus:border-[var(--confcom-blue)]"
              >
                <option value="">Seleziona un corso...</option>
                {CORSI.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.titolo}</option>
                ))}
              </select>
            </div>
          </Section>
        )}

        {/* Profilazione */}
        <Section icon={UserCheck} title="Sei associato Confcommercio Roma? *" testId="section-profilazione">
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { value: "socio", label: "Socio Confcommercio Roma" },
              { value: "non-socio", label: "Non socio" },
            ].map((opt) => {
              const selected = data.tipo_utente === opt.value;
              return (
                <label
                  key={opt.value}
                  htmlFor={`tipo_utente_${opt.value}`}
                  data-testid={`iscrizione-tipo-utente-${opt.value}`}
                  className={`cursor-pointer rounded-lg border px-4 py-3.5 text-sm font-medium transition-all flex items-center gap-3 ${
                    selected
                      ? "border-[var(--confcom-blue)] bg-[#EBF5FF] text-[var(--confcom-blue)]"
                      : "border-[var(--confcom-border)] bg-white text-[var(--confcom-text-primary)] hover:border-[var(--confcom-blue)]/40"
                  }`}
                >
                  <input
                    id={`tipo_utente_${opt.value}`}
                    type="radio"
                    name="tipo_utente"
                    value={opt.value}
                    checked={selected}
                    onChange={handleChange}
                    required
                    className="accent-[var(--confcom-blue)]"
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </Section>

        {/* Dati anagrafici */}
        <Section icon={User} title="Dati anagrafici" testId="section-anagrafica">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Nome" name="nome" value={data.nome} onChange={handleChange} required />
            <Field label="Cognome" name="cognome" value={data.cognome} onChange={handleChange} required />
            <Field label="Luogo di nascita" name="luogo_nascita" value={data.luogo_nascita} onChange={handleChange} required />
            <Field label="Data di nascita" name="data_nascita" type="date" value={data.data_nascita} onChange={handleChange} required />
            <Field label="Codice fiscale" name="codice_fiscale" value={data.codice_fiscale} onChange={handleChange} required colSpan={2} placeholder="es. RSSMRA80A01H501U" />
          </div>
        </Section>

        {/* Contatti */}
        <Section icon={MapPin} title="Residenza e contatti" testId="section-contatti">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Indirizzo di residenza" name="indirizzo_residenza" value={data.indirizzo_residenza} onChange={handleChange} required colSpan={2} />
            <Field label="CAP di residenza" name="cap_residenza" value={data.cap_residenza} onChange={handleChange} required />
            <Field label="Telefono" name="telefono" type="tel" value={data.telefono} onChange={handleChange} required />
            <Field label="Email" name="email" type="email" value={data.email} onChange={handleChange} required colSpan={2} />
          </div>
        </Section>

        {/* Occupazione */}
        <Section icon={Briefcase} title="Situazione professionale" testId="section-occupazione">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Occupazione attuale" name="occupazione" value={data.occupazione} onChange={handleChange} required colSpan={2} placeholder="es. Impiegato, libero professionista, studente..." />
          </div>
        </Section>

        {/* Fatturazione (opzionale) — collassabile */}
        <section
          className="rounded-2xl bg-white border border-[var(--confcom-border)] mb-6 overflow-hidden"
          data-testid="section-fatturazione"
        >
          <button
            type="button"
            onClick={() => setFatturazioneOpen((v) => !v)}
            aria-expanded={fatturazioneOpen}
            aria-controls="fatturazione-panel"
            data-testid="section-fatturazione-toggle"
            className="w-full flex items-center gap-3 p-6 md:p-8 text-left hover:bg-[#F5F7FA] transition-colors"
          >
            <div className="w-9 h-9 rounded-lg bg-[#EBF5FF] flex items-center justify-center shrink-0">
              <Building2 size={18} className="text-[var(--confcom-blue)]" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg md:text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">
                Fatturazione a società (opzionale)
              </h2>
              <p className="text-sm text-[var(--confcom-text-secondary)] mt-0.5">
                {fatturazioneOpen ? "Compila i dati aziendali qui sotto" : "Espandi solo se la fattura va intestata a un'impresa"}
              </p>
            </div>
            <ChevronDown
              size={20}
              className={`text-[var(--confcom-blue)] shrink-0 transition-transform ${fatturazioneOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
          {fatturazioneOpen && (
            <div id="fatturazione-panel" className="px-6 md:px-8 pb-6 md:pb-8 border-t border-[var(--confcom-border)] pt-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Ragione sociale" name="ragione_sociale" value={data.ragione_sociale} onChange={handleChange} colSpan={2} />
                <Field label="Indirizzo sede legale" name="sede_legale" value={data.sede_legale} onChange={handleChange} colSpan={2} />
                <Field label="Telefono azienda" name="telefono_azienda" type="tel" value={data.telefono_azienda} onChange={handleChange} />
                <Field label="PEC" name="pec" type="email" value={data.pec} onChange={handleChange} />
                <Field label="Codice SDI" name="codice_sdi" value={data.codice_sdi} onChange={handleChange} />
                <Field label="P.IVA / CF" name="piva_cf" value={data.piva_cf} onChange={handleChange} />
                <Field label="Referente" name="referente" value={data.referente} onChange={handleChange} colSpan={2} />
                <Field label="Telefono referente" name="telefono_referente" type="tel" value={data.telefono_referente} onChange={handleChange} />
                <Field label="Email referente" name="email_referente" type="email" value={data.email_referente} onChange={handleChange} />
              </div>
            </div>
          )}
        </section>

        {/* Come ci hai conosciuto (in fondo) */}
        <Section icon={HelpCircle} title="Provenienza" testId="section-provenienza">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Come ci hai conosciuto?" name="come_ci_hai_conosciuto" value={data.come_ci_hai_conosciuto} onChange={handleChange} required colSpan={2} placeholder="es. Sito web, associato, passaparola, social..." />
          </div>
        </Section>

        {errorMsg && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6" data-testid="iscrizione-error">
            {errorMsg}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--confcom-border)] bg-white text-[var(--confcom-text-primary)] text-sm font-semibold px-5 py-2.5 hover:bg-[#F5F7FA] transition-colors"
            data-testid="iscrizione-cancel"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--confcom-blue)] text-white text-sm font-semibold px-6 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="iscrizione-submit"
          >
            {submitting ? "Invio in corso..." : (<><Send size={16} /> Invia iscrizione</>)}
          </button>
        </div>
      </form>
    </main>
  );
}
